// Shared fetch helpers for the search widget (#8167).
//
// Every network call the widget makes goes through here so timeouts and the
// keepalive-on-click behavior are consistent and testable. A hung config or
// query request rejects after its budget instead of leaving the widget
// spinning forever; a click/tracking request is fire-and-forget with
// keepalive so it survives the navigation it triggers and never throws.

// Per-call timeout budgets in milliseconds.
export const FETCH_TIMEOUTS = {
  config: 8000,
  query: 8000,
  click: 3000,
};

// Client-error beacon (#8172): fire-and-forget failure reporting to the
// endpoint added in #8166. Reports are capped per page load, never carry the
// query text or a token in the body, and never throw.
export const CLIENT_ERROR_ENDPOINT = '/api/search/query/client-error';
export const MAX_CLIENT_ERROR_BEACONS = 5;
// Widget major version, sent as widgetVersion so backend logs can attribute a
// beacon to a widget release.
export const WIDGET_VERSION = '5';

let clientErrorBeaconCount = 0;

// resetClientErrorBeacons restores the per-page beacon budget. Exported for
// tests; the widget itself never resets within a single page load.
export function resetClientErrorBeacons() {
  clientErrorBeaconCount = 0;
}

/**
 * sendClientErrorBeacon fires a single client-error report and returns whether
 * it was dispatched (false once the per-page cap is reached). It is
 * fire-and-forget: keepalive so it survives the navigation it may accompany,
 * wrapped so it never throws, and it never puts the query text or a token in
 * the payload body. The public token travels only in the Authorization header
 * the endpoint requires; pagePath carries no query string.
 *
 * @param {Object} params
 * @param {string} [params.baseUrl]
 * @param {string} [params.token] - client public token (Authorization header only)
 * @param {string} params.errorClass - one of the backend's known classes
 * @param {number} [params.status]
 * @param {string} [params.message]
 * @returns {boolean} true if a beacon was dispatched
 */
export function sendClientErrorBeacon({ baseUrl = '', token, errorClass, status = 0, message = '' } = {}) {
  try {
    if (clientErrorBeaconCount >= MAX_CLIENT_ERROR_BEACONS) {
      return false;
    }
    clientErrorBeaconCount++;

    const loc = typeof window !== 'undefined' ? window.location : null;
    const payload = {
      errorClass: errorClass || 'other',
      status: Number.isFinite(status) ? status : 0,
      message: typeof message === 'string' ? message.slice(0, 256) : '',
      widgetVersion: WIDGET_VERSION,
      pageHost: loc ? loc.host : '',
      pagePath: loc ? loc.pathname : '', // pathname only -- never a query string
    };

    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = token;
    }

    const result = fetch(`${baseUrl}${CLIENT_ERROR_ENDPOINT}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      keepalive: true,
    });
    if (result && typeof result.catch === 'function') {
      result.catch(() => {}); // reporting must never surface a failure
    }
    return true;
  } catch (_error) {
    // A beacon can never break the caller's failure path.
    return false;
  }
}

/**
 * fetchWithTimeout wraps fetch with an AbortSignal that fires after
 * timeoutMs, so a request that never responds rejects (AbortError) instead
 * of hanging. The timer is always cleared, and a caller-supplied signal is
 * respected in addition to the timeout when the runtime supports
 * AbortSignal.any.
 *
 * @param {string} url
 * @param {RequestInit} [options]
 * @param {number} [timeoutMs]
 * @returns {Promise<Response>}
 */
export async function fetchWithTimeout(url, options = {}, timeoutMs = FETCH_TIMEOUTS.query) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let signal = controller.signal;
  if (options.signal && typeof AbortSignal !== 'undefined' && typeof AbortSignal.any === 'function') {
    signal = AbortSignal.any([options.signal, controller.signal]);
  }

  try {
    return await fetch(url, { ...options, signal });
  } finally {
    clearTimeout(timer);
  }
}

// sleep resolves after ms milliseconds, or immediately when ms is not
// positive. Kept local so callers (and tests via backoffMs:0) can opt out of a
// real timer entirely.
const sleep = (ms) => (ms > 0 ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve());

/**
 * fetchConfig loads the widget's index config, treating a non-2xx response as
 * a failure (a JSON body alone is not success) and retrying once after a short
 * backoff before giving up. On final failure it throws so the caller can render
 * an error state instead of silently vanishing. A fresh request body is built
 * per attempt via buildBody, since a FormData/stream body cannot be replayed.
 *
 * @param {string} url
 * @param {() => BodyInit} buildBody - factory returning a fresh POST body per attempt
 * @param {Object} [opts]
 * @param {number} [opts.retries=1] - extra attempts after the first
 * @param {number} [opts.backoffMs=400] - base backoff, scaled by attempt number
 * @returns {Promise<Object>} parsed config JSON
 * @throws {Error} when every attempt fails
 */
export async function fetchConfig(url, buildBody, { retries = 1, backoffMs = 400 } = {}) {
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt > 0) {
      await sleep(backoffMs * attempt);
    }

    try {
      const response = await fetchWithTimeout(
        url,
        { method: 'POST', body: buildBody() },
        FETCH_TIMEOUTS.config
      );

      if (!response.ok) {
        lastError = new Error(`config fetch failed: ${response.status}`);
        lastError.status = response.status;
        continue;
      }

      return await response.json();
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error('config fetch failed');
}

/**
 * clickFetch sends a fire-and-forget click/tracking request. It sets
 * keepalive so the request survives the page navigation it usually
 * precedes, caps it at the click timeout, and never throws or rejects:
 * tracking must never break the user's click. Returns the Response on
 * success or undefined on any failure.
 *
 * @param {string} url
 * @param {RequestInit} [options]
 * @returns {Promise<Response|undefined>}
 */
export async function clickFetch(url, options = {}) {
  try {
    return await fetchWithTimeout(url, { ...options, keepalive: true }, FETCH_TIMEOUTS.click);
  } catch (_error) {
    return undefined;
  }
}

// One embed should resolve its index config once. The bootstrap, the shared
// overlay root and every placement root all ask for the same config with the
// same token, so without sharing a page with N placements makes N+1 identical
// POSTs -- and multiple placements are now the normal case rather than the
// exception.
//
// Keyed by url+token because those are exactly what determine the answer.
const sharedConfigPromises = new Map();

/**
 * Fetch an index config, sharing one request across every caller using the same
 * token. Callers racing before the first answer arrives all await the same
 * promise rather than each starting their own request.
 *
 * A rejection is NOT cached: sharing must not turn one transient failure into a
 * permanently broken widget for every placement on the page, so the next caller
 * retries from scratch.
 *
 * @param {string} url - config endpoint
 * @param {string} token - the embed's client public token
 * @param {function(): FormData} buildBody - request body factory, per attempt
 * @param {Object} [options] - passed through to fetchConfig (retries, backoffMs)
 * @returns {Promise<Object>} the parsed config
 */
export function fetchSharedConfig(url, token, buildBody, options) {
  const key = `${url}::${token}`;
  const existing = sharedConfigPromises.get(key);
  if (existing) return existing;

  const pending = fetchConfig(url, buildBody, options).catch((error) => {
    sharedConfigPromises.delete(key);
    throw error;
  });
  sharedConfigPromises.set(key, pending);
  return pending;
}

/**
 * Drop every shared config promise. Exists for tests and for the first-party
 * remount hook, which rebuilds the widget and must not serve a stale config.
 */
export function resetSharedConfigCache() {
  sharedConfigPromises.clear();
}
